// "Errors today" — per-service request / error rollup for the current UTC day.
// /api/errors now returns [{ service, requests, errors, uptimePct }] (already
// filtered to services with traffic or errors), so we render that directly:
// Service · Requests (big) · Errors (red if >0) · Uptime%. Self-fetching,
// handles loading / empty / error and dark mode. Refetches when `refreshTick`
// changes (Dashboard's 60s auto-refresh / manual refresh).
import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type ServiceErrorRow } from "../lib/api";
import { Card, SectionTitle } from "./ui";
import { big, pct, serviceLabel } from "../lib/format";

/** Today as YYYY-MM-DD in UTC (the API keys errors by UTC day). */
function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function ErrorsToday({ refreshTick }: { refreshTick?: number }) {
  const [rows, setRows] = useState<ServiceErrorRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .errors(todayUtc())
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : "Failed to load errors");
      });
    return () => {
      alive = false;
    };
  }, [refreshTick]);

  // Zone-level rollup (_account) goes last and is rendered muted.
  const sorted = useMemo(
    () =>
      [...(rows ?? [])].sort(
        (a, b) =>
          (a.service === "_account" ? 1 : 0) - (b.service === "_account" ? 1 : 0),
      ),
    [rows],
  );

  const hasRows = sorted.length > 0;

  return (
    <section>
      <SectionTitle>Errors today</SectionTitle>
      <Card className="overflow-x-auto p-0">
        {error ? (
          <div className="p-4 text-sm text-red-600 dark:text-red-400">⚠️ {error}</div>
        ) : rows === null ? (
          <div className="p-4 text-sm text-gray-400 dark:text-gray-500">Loading…</div>
        ) : !hasRows ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">✅ No errors today.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="px-4 py-2 font-medium">Service</th>
                <th className="px-4 py-2 text-right font-medium">Requests</th>
                <th className="px-4 py-2 text-right font-medium">Errors</th>
                <th className="px-4 py-2 text-right font-medium">Uptime</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const errCount = r.errors ?? 0;
                const isZone = r.service === "_account";
                return (
                  <tr
                    key={r.service}
                    className="border-b border-gray-100 last:border-0 dark:border-gray-700/60"
                  >
                    <td
                      className={
                        "px-4 py-2 font-medium " +
                        (isZone
                          ? "text-gray-400 dark:text-gray-500"
                          : "text-gray-900 dark:text-gray-100")
                      }
                    >
                      {serviceLabel(r.service)}
                    </td>
                    <td
                      className={
                        "px-4 py-2 text-right text-base font-semibold tabular-nums " +
                        (isZone
                          ? "text-gray-400 dark:text-gray-500"
                          : "text-gray-900 dark:text-gray-100")
                      }
                    >
                      {big(r.requests)}
                    </td>
                    <td
                      className={
                        "px-4 py-2 text-right font-semibold tabular-nums " +
                        (isZone
                          ? "text-gray-400 dark:text-gray-500"
                          : errCount > 0
                            ? "text-red-600 dark:text-red-400"
                            : "text-green-600 dark:text-green-400")
                      }
                    >
                      {big(errCount)}
                    </td>
                    <td
                      className={
                        "px-4 py-2 text-right tabular-nums " +
                        (isZone
                          ? "text-gray-400 dark:text-gray-500"
                          : "text-gray-700 dark:text-gray-300")
                      }
                    >
                      {r.uptimePct === null || r.uptimePct === undefined
                        ? "—"
                        : pct(r.uptimePct / 100, 1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </section>
  );
}
